/**
 * Persistence layer and global state — Suturia (§14).
 * tRPC backend (surgeries/profiles) per authenticated user + offline cache in
 * localStorage (suturia:*:v1 keys). Signed out (or offline) → 100% local guest
 * mode with the same hooks API.
 *
 * Sync rules (docs/persistence-audit-2026-07-24.md):
 * - When signed in, `surgeries.listMine` (summary payloads) and profile data
 *   from `auth.bootstrap` / `profiles.mine` are fetched on load. Summaries
 *   merge with local full records; detail pages hydrate via `surgeries.getById`.
 * - First sync of an empty account with a local archive present: the local
 *   data is UPLOADED (bulkUpsert) instead of letting the server overwrite it.
 * - Login reconciliation by cache ownership (suturia:account:v1): same
 *   account → server wins; no account on record → guest work merge-uploads;
 *   different account → the cache is purged first (cross-account leak guard).
 * - Demo ("sample data") records are ephemeral and client-only: flagged
 *   `demo: true` (legacy ones by their `semilla-*` ids), never uploaded, and
 *   purged on login. Logout clears per-user cached state.
 * - Optimistic mutations: the UI updates instantly and the operation is
 *   replicated to the backend; if it fails (offline), the next server read
 *   wins.
 *
 * The backend stores the full Surgery document in the `payload` column; the
 * flat columns (id, folio, fecha, year, categoria) are derived from it.
 *
 * A one-time migration moves the legacy "bq:*" keys (pre-rebrand builds)
 * into the new "suturia:*" keys and normalizes the stored Spanish enum
 * values (tecnica/rol, view prefs) to the current English ones.
 *
 * Usage:
 *   <SuturiaProvider> … <App/> … </SuturiaProvider>
 *   const { surgeries, createSurgery } = useSurgeries();
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { nanoid } from 'nanoid';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { isSurgeryDetailThin, mergeSurgerySummary } from '@/lib/surgery-detail';
import { isPatientIdSealed, sealPatientIdFields } from '@/lib/patient-id';
import { getPatientIdDek } from '@/lib/patient-id-dek';
import type {
  ExportFile,
  Surgery,
  Ficha,
  Profile,
  Prefs,
} from './types';
import {
  EMPTY_PROFILE,
  INITIAL_PREFS,
  normalizeCareerEntryKind,
  normalizeLibraryView,
  normalizeSurgeryEnums,
  normalizeVolumeMode,
  normalizeYearFilter,
} from './types';
import type { CustomProcedure } from './catalog';
import { buildSeed } from './seed';
import {
  KEY_ACCOUNT,
  decideLoginReconcile,
  isDemoSurgery,
  mergeAnonymousIntoServer,
  stripDemoSurgeries,
  type LoginReconcile,
} from './persistence';

export const KEY_SURGERIES = 'suturia:surgeries:v1';
export const KEY_PROFILE = 'suturia:profile:v1';
export const KEY_PREFS = 'suturia:prefs:v1';
export const KEY_CUSTOM_PROCEDURES = 'suturia:custom-procedures:v1';
/** Unsent new-surgery draft (owned by pages/NewSurgery.tsx) — can hold
    clinical notes, so it is purged on logout and account-switch (audit §6). */
export const KEY_DRAFT = 'suturia:draft:v1';

/* ───────────────────── one-time legacy migration (bq:* → suturia:*) ───────────────────── */

/**
 * Legacy key → current key. The draft and ficha-source keys are owned by
 * other modules (surgery form page, fichaSync); they are listed here so the
 * migration runs once, before any module reads storage.
 */
const LEGACY_KEY_MAP: [string, string][] = [
  ['bq:cirugias:v1', KEY_SURGERIES],
  ['bq:perfil:v1', KEY_PROFILE],
  ['bq:prefs:v1', KEY_PREFS],
  ['bq:borrador:v1', KEY_DRAFT],
  ['bq:ficha-fuente:v1', 'suturia:ficha-source:v1'],
];

const LEGACY_SESSION_KEY_MAP: [string, string][] = [
  ['bq:biblioteca:estado', 'suturia:library:state'],
];

/** Normalizes stored values that used Spanish enum literals. */
function migrateValue(key: string, raw: string): string {
  try {
    if (key === KEY_SURGERIES) {
      const list = JSON.parse(raw) as unknown;
      if (!Array.isArray(list)) return raw;
      return JSON.stringify(list.map((c) => normalizeSurgeryEnums(c as Surgery)));
    }
    if (key === KEY_PREFS) {
      const prefs = JSON.parse(raw) as Record<string, unknown>;
      // Drop legacy UI language override — language follows the browser only.
      const rest = { ...prefs };
      delete rest.idioma;
      return JSON.stringify({
        ...rest,
        ultimoAnio: normalizeYearFilter(rest.ultimoAnio),
        vistaBiblioteca: normalizeLibraryView(rest.vistaBiblioteca),
        volumenModo: normalizeVolumeMode(rest.volumenModo),
      });
    }
  } catch {
    // unparsable value: moved as-is; readers fall back to defaults
  }
  return raw;
}

function migrateKeyMap(storage: Storage, map: [string, string][]): void {
  for (const [legacy, current] of map) {
    const raw = storage.getItem(legacy);
    if (raw === null) continue;
    try {
      if (storage.getItem(current) === null) {
        storage.setItem(current, migrateValue(current, raw));
      }
      storage.removeItem(legacy);
    } catch {
      // storage unavailable: leave everything untouched
    }
  }
}

/**
 * Moves every legacy "bq:*" key to its "suturia:*" counterpart, migrating
 * stored Spanish enum values. Idempotent; runs once at module load.
 */
export function migrateLegacyStorage(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    migrateKeyMap(localStorage, LEGACY_KEY_MAP);
    migrateKeyMap(sessionStorage, LEGACY_SESSION_KEY_MAP);
  } catch {
    // storage unavailable (private mode, SSR): the app runs on defaults
  }
}

migrateLegacyStorage();

/* ────────────────────────── localStorage helpers ────────────────────────── */

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...JSON.parse(raw) } as T;
  } catch {
    return fallback;
  }
}

function readArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? (data as T[]) : [];
  } catch {
    return [];
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or unavailable: the write is lost, the app keeps going
  }
}

/* ───────────── account-scoped cache bookkeeping (persistence.ts rules) ───────────── */

/** The account (users.id as string) the per-user cache currently belongs to. */
function readStoredAccount(): string | null {
  try {
    return localStorage.getItem(KEY_ACCOUNT);
  } catch {
    return null;
  }
}

function writeStoredAccount(account: string): void {
  try {
    localStorage.setItem(KEY_ACCOUNT, account);
  } catch {
    // storage unavailable: reconciliation falls back to anonymous-merge next login
  }
}

function clearStoredAccount(): void {
  try {
    localStorage.removeItem(KEY_ACCOUNT);
  } catch {
    // storage unavailable
  }
}

/**
 * Removes per-user data that no React state mirrors back into storage — today
 * the new-surgery draft, which can hold clinical notes (persistence audit
 * §6). Called on logout and on account-switch. Tested with a fake Storage.
 */
export function purgeUnmirroredPerUserData(
  storage: Pick<Storage, 'removeItem'> | undefined = typeof localStorage !== 'undefined'
    ? localStorage
    : undefined,
): void {
  try {
    storage?.removeItem(KEY_DRAFT);
  } catch {
    // storage unavailable
  }
}

/* ────────────────────────── sequential folios ────────────────────────── */

/** Accepts the current "SUT-" prefix plus legacy "CHI-"/"CIR-" folios. */
const RE_FOLIO = /^(?:SUT|CHI|CIR)-(\d{4})-(\d{4})$/;

/** Next sequential folio for the given year: SUT-YYYY-NNNN */
export function nextFolio(surgeries: Surgery[], year: number): string {
  let max = 0;
  for (const c of surgeries) {
    const m = RE_FOLIO.exec(c.folio);
    if (m && Number(m[1]) === year) max = Math.max(max, Number(m[2]));
  }
  return `SUT-${year}-${String(max + 1).padStart(4, '0')}`;
}

/** Auto patient codes live in `paciente.iniciales` (wire contract unchanged);
    legacy records keep their manually-entered initials. */
const RE_PATIENT_CODE = /^PT-(\d{4})-(\d{4})$/;

/** Next sequential patient code for the given year: PT-YYYY-NNNN */
export function nextPatientCode(surgeries: Surgery[], year: number): string {
  let max = 0;
  for (const c of surgeries) {
    const m = RE_PATIENT_CODE.exec(c.paciente.iniciales);
    if (m && Number(m[1]) === year) max = Math.max(max, Number(m[2]));
  }
  return `PT-${year}-${String(max + 1).padStart(4, '0')}`;
}

/* ────────────────────────── import validation ────────────────────────── */

/**
 * Guarantees the two wire timestamps on an ingested record.
 *
 * `isValidSurgery` deliberately does not require them, so an imported archive
 * (or a payload written by an older build) can arrive without `actualizadoEn`
 * — and anything that sorts on it, like the detail prefetch, would throw and
 * take the whole shell down with it. Missing values fall back to the surgery
 * date so ordering stays meaningful.
 */
function withTimestamps(c: Surgery): Surgery {
  if (typeof c.creadoEn === 'string' && typeof c.actualizadoEn === 'string') return c;
  const fallback = typeof c.fecha === 'string' && c.fecha ? c.fecha : new Date(0).toISOString();
  return {
    ...c,
    creadoEn: typeof c.creadoEn === 'string' ? c.creadoEn : fallback,
    actualizadoEn: typeof c.actualizadoEn === 'string' ? c.actualizadoEn : fallback,
  };
}

/** Every ingest path (localStorage, server, import) goes through this. */
function normalizeIngestedSurgery(c: Surgery): Surgery {
  return withTimestamps(normalizeSurgeryEnums(c));
}

function isValidSurgery(v: unknown): v is Surgery {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c.id === 'string' &&
    typeof c.folio === 'string' &&
    typeof c.fecha === 'string' &&
    typeof c.diagnostico === 'string' &&
    typeof c.categoria === 'string' &&
    typeof c.tecnica === 'string' &&
    typeof c.rol === 'string' &&
    typeof c.duracionMin === 'number' &&
    typeof c.descripcion === 'string' &&
    Array.isArray(c.materiales) &&
    typeof c.paciente === 'object' &&
    c.paciente !== null &&
    typeof c.complicaciones === 'object' &&
    c.complicaciones !== null
  );
}

export interface ImportResult {
  added: number;
  updated: number;
  discarded: number;
}

/* ────────────────────────── backend sync ────────────────────────── */

/**
 * Flat row for `surgeries.upsert`/`bulkUpsert`: the full Surgery document
 * goes into `payload` and the indexing columns are derived from it.
 */
function surgeryRow(c: Surgery) {
  const fechaYear = new Date(c.fecha).getFullYear();
  return {
    id: c.id,
    folio: c.folio,
    fecha: c.fecha,
    year: Number.isNaN(fechaYear) ? new Date().getFullYear() : fechaYear,
    categoria: c.categoria,
    payload: c,
  };
}

/** Batch size for bulkUpsert (the router accepts up to 5000 rows per call) */
const BULK_BATCH = 1000;

/**
 * Flat row for `catalog.upsert`: the full CustomProcedure document goes into
 * `payload` and the indexing columns are derived from it.
 */
function customProcedureRow(p: CustomProcedure) {
  return {
    id: p.id,
    specialty: p.specialtyId,
    name: p.name,
    visibility: p.visibility ?? 'private',
    snomedConceptId: p.snomedConceptId ?? null,
    payload: p,
  };
}

/** Minimal shape check for CustomProcedure docs coming from cache or server. */
function isValidCustomProcedure(v: unknown): v is CustomProcedure {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as Record<string, unknown>;
  return (
    typeof p.id === 'string' &&
    typeof p.name === 'string' &&
    typeof p.specialtyId === 'string' &&
    Array.isArray(p.variants)
  );
}

interface SyncActions {
  upsertSurgery: (c: Surgery) => void;
  removeRemoteSurgery: (id: string) => void;
  bulkSurgeries: (list: Surgery[]) => void;
  syncProfile: (p: Profile) => void;
  upsertCustomProcedure: (p: CustomProcedure) => void;
  removeRemoteCustomProcedure: (id: string) => void;
}

/* ────────────────────────── context types ────────────────────────── */

export type SurgeryInput = Omit<Surgery, 'id' | 'folio' | 'creadoEn' | 'actualizadoEn'>;

/** Chrome-safe surgery fields — Navbar/palette/title without full payload churn. */
export type SurgeryIndexItem = {
  id: string;
  folio: string;
  fecha: string;
  categoria: string;
  diagnostico: string;
  tecnica: string;
  /** Auto patient code (PT-YYYY-NNNN) for palette search. */
  pacienteCodigo: string;
  /** Last-4 hint of the optional sealed patient ID. */
  pacienteIdHint?: string;
};

interface SurgeriesContextValue {
  surgeries: Surgery[];
  createSurgery: (input: SurgeryInput) => Surgery;
  updateSurgery: (id: string, patch: Partial<Omit<Surgery, 'id' | 'folio' | 'creadoEn'>>) => void;
  deleteSurgery: (id: string) => void;
  getSurgery: (id: string) => Surgery | undefined;
  /** Merge a full detail payload (from surgeries.getById) into local state. */
  hydrateSurgery: (surgery: Surgery) => void;
  saveFicha: (id: string, ficha: Ficha | undefined) => void;
  loadSeed: () => number;
  /** Hydrates thin summary rows before writing the archive file. */
  exportFile: () => Promise<string>;
  importFile: (json: string) => ImportResult;
  /** Merge pre-built Surgery documents (career CSV import, etc.). */
  mergeSurgeries: (list: Surgery[]) => ImportResult;
  archiveSummary: { procedures: number; years: number };
}

interface ProfilePrefsContextValue {
  profile: Profile;
  prefs: Prefs;
  saveProfile: (profile: Profile) => void;
  updatePrefs: (patch: Partial<Prefs>) => void;
}

interface CatalogContextValue {
  customProcedures: CustomProcedure[];
  upsertCustomProcedure: (p: CustomProcedure) => void;
  removeCustomProcedure: (id: string) => void;
}

/** @deprecated composite — prefer slice hooks */
type SuturiaContextValue = SurgeriesContextValue & ProfilePrefsContextValue & CatalogContextValue;

const SurgeriesContext = createContext<SurgeriesContextValue | null>(null);
const SurgeryIndexContext = createContext<SurgeryIndexItem[]>([]);
const ProfilePrefsContext = createContext<ProfilePrefsContextValue | null>(null);
const CatalogContext = createContext<CatalogContextValue | null>(null);

const DETAIL_PREFETCH_N = 12;

/* ────────────────────────── provider ────────────────────────── */

export function SuturiaProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const utils = trpc.useUtils();
  // Stable per-account key for cache reconciliation (auth.me exposes users.id).
  const accountKey = user ? String(user.id) : null;

  // Initial state from the local cache: the app renders instantly,
  // even signed out or offline (guest mode).
  const [surgeries, setSurgeries] = useState<Surgery[]>(() =>
    readArray<Surgery>(KEY_SURGERIES).map(normalizeIngestedSurgery),
  );
  const [profile, setProfile] = useState<Profile>(() => readJSON(KEY_PROFILE, EMPTY_PROFILE));
  const [customProcedures, setCustomProcedures] = useState<CustomProcedure[]>(() =>
    readArray<CustomProcedure>(KEY_CUSTOM_PROCEDURES).filter(isValidCustomProcedure),
  );
  const [prefs, setPrefs] = useState<Prefs>(() => {
    const stored = readJSON(KEY_PREFS, INITIAL_PREFS);
    const rest = { ...stored };
    delete rest.idioma;
    return {
      ...rest,
      ultimoAnio: normalizeYearFilter(rest.ultimoAnio),
      vistaBiblioteca: normalizeLibraryView(rest.vistaBiblioteca),
      volumenModo: normalizeVolumeMode(rest.volumenModo),
    };
  });

  /* Refs holding the latest state: they allow reading it outside React
     updaters (sequential folios, import merges, backend uploads). */
  const surgeriesRef = useRef(surgeries);
  const profileRef = useRef(profile);
  const customProceduresRef = useRef(customProcedures);

  // Offline cache: every state change is mirrored to localStorage (suturia:*)
  useEffect(() => {
    surgeriesRef.current = surgeries;
    write(KEY_SURGERIES, surgeries);
  }, [surgeries]);
  useEffect(() => {
    profileRef.current = profile;
    write(KEY_PROFILE, profile);
  }, [profile]);
  useEffect(() => {
    customProceduresRef.current = customProcedures;
    write(KEY_CUSTOM_PROCEDURES, customProcedures);
  }, [customProcedures]);
  useEffect(() => write(KEY_PREFS, prefs), [prefs]);

  /* ── login reconciliation + logout purge (docs/persistence-audit-2026-07-24.md) ── */

  // Consumed by the surgeries sync effect: how the cache reconciles with the
  // account that just logged in.
  const loginModeRef = useRef<LoginReconcile | null>(null);
  // Seed ids already scheduled for remote deletion this session.
  const seedCleanupRef = useRef(new Set<string>());

  // On login: demo records never survive; the cache is then reconciled by
  // account ownership (same-account | anonymous-merge | account-switch).
  useEffect(() => {
    if (!isAuthenticated || !accountKey) {
      loginModeRef.current = null;
      return;
    }
    const mode = decideLoginReconcile(readStoredAccount(), accountKey);
    loginModeRef.current = mode;
    if (mode === 'account-switch') {
      // The cache belongs to a DIFFERENT account: drop it before the new
      // account's server data loads (cross-account leak guard).
      setSurgeries([]);
      setProfile(EMPTY_PROFILE);
      setCustomProcedures([]);
      setPrefs((p) => ({ ...p, tourSeenVersion: undefined }));
      purgeUnmirroredPerUserData(); // the draft may hold clinical notes
    } else {
      setSurgeries((prev) => stripDemoSurgeries(prev));
    }
    writeStoredAccount(accountKey);
  }, [isAuthenticated, accountKey]);

  // Seal any legacy plaintext patient IDs still in the local cache (and push
  // the sealed payload so Postgres never keeps cleartext either).
  useEffect(() => {
    if (!isAuthenticated || !accountKey) return;

    let cancelled = false;
    void (async () => {
      const current = surgeriesRef.current;
      const needsSeal = current.filter((s) => {
        const hc = s.paciente.historiaClinica?.trim();
        return Boolean(hc) && !isPatientIdSealed(hc);
      });
      if (needsSeal.length === 0 || cancelled) return;

      const dek = getPatientIdDek();
      const sealedList = await Promise.all(
        needsSeal.map(async (s) => {
          const sealedFields = await sealPatientIdFields({
            rawInput: s.paciente.historiaClinica ?? '',
            accountKey,
            dek,
            previous: s.paciente,
          });
          return {
            ...s,
            paciente: { ...s.paciente, ...sealedFields },
            actualizadoEn: new Date().toISOString(),
          } satisfies Surgery;
        }),
      );
      if (cancelled) return;
      const nextById = new Map(current.map((s) => [s.id, s]));
      for (const updated of sealedList) nextById.set(updated.id, updated);
      setSurgeries([...nextById.values()]);
      for (const s of sealedList) {
        actionsRef.current.upsertSurgery(s);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accountKey, surgeries]);

  // On logout (incl. session expiry): clear per-user data so the next session
  // on this browser — guest or another account — never sees it.
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      setSurgeries([]);
      setProfile(EMPTY_PROFILE);
      setCustomProcedures([]);
      setPrefs((p) => ({ ...p, tourSeenVersion: undefined }));
      clearStoredAccount();
      purgeUnmirroredPerUserData(); // the draft may hold clinical notes
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  /* ── tRPC mutations (only fired when signed in) ── */

  const upsertMut = trpc.surgeries.upsert.useMutation({
    onSettled: () => void utils.surgeries.listMine.invalidate(),
  });
  const removeMut = trpc.surgeries.remove.useMutation({
    onSettled: () => void utils.surgeries.listMine.invalidate(),
  });
  const bulkMut = trpc.surgeries.bulkUpsert.useMutation({
    onSettled: () => void utils.surgeries.listMine.invalidate(),
  });
  const profileMut = trpc.profiles.upsert.useMutation({
    onSettled: () => {
      void utils.profiles.mine.invalidate();
      void utils.auth.bootstrap.invalidate();
    },
  });
  const catalogUpsertMut = trpc.catalog.upsert.useMutation({
    onSettled: () => void utils.catalog.list.invalidate(),
  });
  const catalogRemoveMut = trpc.catalog.remove.useMutation({
    onSettled: () => void utils.catalog.list.invalidate(),
  });

  /* Sync actions read via ref: the public callbacks keep a stable identity
     and always invoke the latest closure. */
  const actionsRef = useRef<SyncActions>({
    upsertSurgery: () => {},
    removeRemoteSurgery: () => {},
    bulkSurgeries: () => {},
    syncProfile: () => {},
    upsertCustomProcedure: () => {},
    removeRemoteCustomProcedure: () => {},
  });

  useEffect(() => {
    actionsRef.current = {
      upsertSurgery: (c) => {
        if (isAuthenticated) upsertMut.mutate(surgeryRow(c));
      },
      removeRemoteSurgery: (id) => {
        if (isAuthenticated) removeMut.mutate({ id });
      },
      bulkSurgeries: (list) => {
        if (!isAuthenticated || list.length === 0) return;
        for (let i = 0; i < list.length; i += BULK_BATCH) {
          bulkMut.mutate({ rows: list.slice(i, i + BULK_BATCH).map(surgeryRow) });
        }
      },
      syncProfile: (p) => {
        const nombre = p.nombre.trim();
        const especialidad = p.especialidad.trim();
        // The backend schema requires non-empty name and specialty
        if (!isAuthenticated || !nombre || !especialidad) return;
        profileMut.mutate({
          displayName: nombre,
          specialty: especialidad,
          subspecialty: p.subespecialidad?.trim() || null,
          hospital: p.centro.trim() || null,
          city: p.ciudad?.trim() || null,
          country: p.pais?.trim() || null,
          region: p.region?.trim() || null,
          birthYear: p.anioNacimiento ?? null,
          careerStartYear: p.anioInicio ?? null,
          stageHistory: p.etapas?.map((e) => ({ stage: e.etapa, fromYear: e.desdeAnio })) ?? null,
          careerEntries: (p.trayectoria ?? [])
            .filter((e) => e.institution.trim())
            .map((e) => ({
              id: e.id,
              kind: normalizeCareerEntryKind(e.kind),
              institution: e.institution.trim(),
              title: e.title?.trim() || undefined,
              specialty: e.specialty?.trim() || undefined,
              fromYear: e.fromYear,
              toYear: e.toYear ?? null,
              current: e.current,
            })),
          bio: p.bio?.trim() || null,
          isPublic: Boolean(p.publico),
        });
      },
      upsertCustomProcedure: (p) => {
        if (isAuthenticated) catalogUpsertMut.mutate(customProcedureRow(p));
      },
      removeRemoteCustomProcedure: (id) => {
        if (isAuthenticated) catalogRemoveMut.mutate({ id });
      },
    };
  });

  /* ── backend queries (only when signed in) ── */

  const surgeriesQuery = trpc.surgeries.listMine.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: 1,
  });
  // Profile: bootstrap already carries the row for onboarding; keep mine for
  // post-mutation refresh without blocking the shell.
  const profileQuery = trpc.profiles.mine.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 120_000,
    retry: 1,
  });
  const catalogQuery = trpc.catalog.list.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 120_000,
    retry: 1,
  });

  /* First upload of an empty account: if the server has nothing and the
     local archive does, the local data is migrated instead of deleted. */
  const surgeriesMigrated = useRef(false);
  const profileMigrated = useRef(false);
  const catalogMigrated = useRef(false);

  // The server wins: fresh data replaces local state when it arrives —
  // except for anonymous guest work, which merge-uploads (persistence.ts).
  useEffect(() => {
    if (!isAuthenticated) {
      surgeriesMigrated.current = false;
      return;
    }
    const rows = surgeriesQuery.data;
    if (!rows) return;

    // One-time repair of legacy contamination: demo rows uploaded to the
    // account by older builds (stable `semilla-*` ids) are deleted remotely
    // and dropped locally on sight.
    for (const r of rows) {
      if (r.id.startsWith('semilla-') && !seedCleanupRef.current.has(r.id)) {
        seedCleanupRef.current.add(r.id);
        actionsRef.current.removeRemoteSurgery(r.id);
      }
    }

    const summaries = rows
      .map((r) => r.payload)
      .filter(isValidSurgery)
      .map(normalizeIngestedSurgery)
      .filter((c) => !isDemoSurgery(c));
    const local = surgeriesRef.current;
    if (summaries.length === 0 && local.length > 0) {
      if (!surgeriesMigrated.current) {
        surgeriesMigrated.current = true;
        const cleaned = stripDemoSurgeries(local);
        setSurgeries(cleaned);
        actionsRef.current.bulkSurgeries(cleaned);
      }
      return; // never overwrite the local archive with an empty remote list
    }
    const localById = new Map(local.map((c) => [c.id, c]));
    const mergedSummaries = summaries.map((s) =>
      mergeSurgerySummary(localById.get(s.id), s),
    );
    if (loginModeRef.current === 'anonymous-merge') {
      // Guest work predating the account: upload local-only rows instead of
      // wiping them, then the server is authoritative from here on.
      loginModeRef.current = null;
      const { merged, toUpload } = mergeAnonymousIntoServer(local, mergedSummaries);
      if (toUpload.length > 0) actionsRef.current.bulkSurgeries(toUpload);
      setSurgeries(merged);
      return;
    }
    // Keep local-only rows (not yet on server) that aren't demo.
    const remoteIds = new Set(mergedSummaries.map((c) => c.id));
    const localOnly = local.filter((c) => !remoteIds.has(c.id) && !isDemoSurgery(c));
    setSurgeries([...mergedSummaries, ...localOnly]);
  }, [surgeriesQuery.data, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      profileMigrated.current = false;
      return;
    }
    if (!profileQuery.isSuccess) return;
    const row = profileQuery.data;
    if (!row) {
      // No remote profile yet: keep the local one and upload it if usable
      const p = profileRef.current;
      if (!profileMigrated.current && p.nombre.trim() && p.especialidad.trim()) {
        profileMigrated.current = true;
        actionsRef.current.syncProfile(p);
      }
      return;
    }
    queueMicrotask(() => {
      setProfile({
        nombre: row.displayName,
        especialidad: row.specialty,
        centro: row.hospital ?? '',
        subespecialidad: row.subspecialty ?? '',
        ciudad: row.city ?? '',
        pais: row.country ?? '',
        region: row.region ?? '',
        anioNacimiento: row.birthYear ?? undefined,
        anioInicio: row.careerStartYear ?? undefined,
        bio: row.bio ?? '',
        publico: Boolean(row.isPublic),
        etapas: Array.isArray(row.stageHistory)
          ? row.stageHistory
              .filter(
                (s): s is { stage: 'student' | 'resident' | 'attending'; fromYear: number } =>
                  typeof s === 'object' && s !== null,
              )
              .map((s) => ({ etapa: s.stage, desdeAnio: s.fromYear }))
          : undefined,
        trayectoria: Array.isArray(row.careerEntries)
          ? (row.careerEntries as Array<Record<string, unknown>>)
              .filter(
                (e) =>
                  typeof e === 'object' &&
                  e !== null &&
                  typeof e.id === 'string' &&
                  typeof e.institution === 'string',
              )
              .map((e) => ({
                id: e.id as string,
                kind: normalizeCareerEntryKind(e.kind),
                institution: e.institution as string,
                title: typeof e.title === 'string' ? e.title : undefined,
                specialty: typeof e.specialty === 'string' ? e.specialty : undefined,
                fromYear: typeof e.fromYear === 'number' ? e.fromYear : new Date().getFullYear(),
                toYear: typeof e.toYear === 'number' ? e.toYear : null,
                current: Boolean(e.current),
              }))
          : undefined,
      });
    });
  }, [profileQuery.data, profileQuery.isSuccess, isAuthenticated]);

  // The server wins: fresh data replaces local state when it arrives.
  useEffect(() => {
    if (!isAuthenticated) {
      catalogMigrated.current = false;
      return;
    }
    const cards = catalogQuery.data;
    if (!cards) return;
    if (cards.length === 0 && customProceduresRef.current.length > 0) {
      if (!catalogMigrated.current) {
        catalogMigrated.current = true;
        for (const p of customProceduresRef.current) {
          actionsRef.current.upsertCustomProcedure(p);
        }
      }
      return; // never overwrite the local list with an empty remote list
    }
    const localById = new Map(customProceduresRef.current.map((p) => [p.id, p]));
    const fromServer: CustomProcedure[] = [];
    for (const card of cards) {
      const local = localById.get(card.id);
      if (local && isValidCustomProcedure(local)) {
        fromServer.push({
          ...local,
          name: card.name,
          specialtyId: card.specialtyId,
          visibility: card.visibility,
          ...(card.snomedConceptId ? { snomedConceptId: card.snomedConceptId } : {}),
        });
        continue;
      }
      // Stub from list card — full variants arrive via getById / matchTemplates / upsert.
      fromServer.push({
        id: card.id,
        name: card.name,
        specialtyId: card.specialtyId,
        visibility: card.visibility,
        ...(card.snomedConceptId ? { snomedConceptId: card.snomedConceptId } : {}),
        variants: [
          {
            id: 'preview',
            name: card.name,
            tecnica: 'Other',
            materiales: card.materialesPreview ?? [],
            ...(card.pasosPreview?.length ? { pasos: card.pasosPreview } : {}),
          },
        ],
      });
    }
    setCustomProcedures(fromServer);
  }, [catalogQuery.data, isAuthenticated]);

  /* ── public API (optimistic: instant local + backend replica) ── */

  const createSurgery = useCallback((input: SurgeryInput): Surgery => {
    const now = new Date().toISOString();
    const year = new Date(input.fecha).getFullYear();
    const created: Surgery = {
      ...input,
      id: nanoid(),
      folio: nextFolio(surgeriesRef.current, Number.isNaN(year) ? new Date().getFullYear() : year),
      creadoEn: now,
      actualizadoEn: now,
    };
    setSurgeries((prev) => [...prev, created]);
    actionsRef.current.upsertSurgery(created);
    return created;
  }, []);

  const updateSurgery = useCallback(
    (id: string, patch: Partial<Omit<Surgery, 'id' | 'folio' | 'creadoEn'>>) => {
      const current = surgeriesRef.current.find((c) => c.id === id);
      if (!current) return;
      const merged: Surgery = { ...current, ...patch, actualizadoEn: new Date().toISOString() };
      setSurgeries((prev) => prev.map((c) => (c.id === id ? merged : c)));
      actionsRef.current.upsertSurgery(merged);
    },
    [],
  );

  const hydrateSurgery = useCallback((surgery: Surgery) => {
    if (!isValidSurgery(surgery)) return;
    const normalized = normalizeIngestedSurgery(surgery);
    setSurgeries((prev) => {
      const idx = prev.findIndex((c) => c.id === normalized.id);
      if (idx === -1) return [...prev, normalized];
      const next = prev.slice();
      next[idx] = normalized;
      return next;
    });
  }, []);

  const deleteSurgery = useCallback((id: string) => {
    setSurgeries((prev) => prev.filter((c) => c.id !== id));
    actionsRef.current.removeRemoteSurgery(id);
  }, []);

  const getSurgery = useCallback(
    (id: string) => surgeries.find((c) => c.id === id),
    [surgeries],
  );

  const saveFicha = useCallback((id: string, ficha: Ficha | undefined) => {
    const current = surgeriesRef.current.find((c) => c.id === id);
    if (!current) return;
    const merged: Surgery = { ...current, ficha, actualizadoEn: new Date().toISOString() };
    setSurgeries((prev) => prev.map((c) => (c.id === id ? merged : c)));
    actionsRef.current.upsertSurgery(merged);
  }, []);

  const loadSeed = useCallback((): number => {
    const seed = buildSeed();
    const ids = new Set(surgeriesRef.current.map((c) => c.id));
    const fresh = seed.filter((c) => !ids.has(c.id));
    if (fresh.length > 0) {
      // Demo records are ephemeral and client-only (persistence.ts): never
      // uploaded, and replaced by the real server data on login.
      setSurgeries((prev) => [...prev, ...fresh]);
    }
    setPrefs((p) => ({ ...p, semillaCargada: true }));
    return fresh.length;
  }, []);

  const saveProfile = useCallback((p: Profile) => {
    setProfile(p);
    actionsRef.current.syncProfile(p); // profiles.upsert when signed in
  }, []);

  const updatePrefs = useCallback((patch: Partial<Prefs>) => {
    // idioma is deprecated — never persist a language override.
    const safe = { ...patch };
    delete safe.idioma;
    setPrefs((p) => {
      const next = { ...p, ...safe };
      delete next.idioma;
      return next;
    });
  }, []);

  const upsertCustomProcedure = useCallback((p: CustomProcedure) => {
    setCustomProcedures((prev) => {
      const idx = prev.findIndex((c) => c.id === p.id);
      if (idx === -1) return [...prev, p];
      const copy = [...prev];
      copy[idx] = p;
      return copy;
    });
    actionsRef.current.upsertCustomProcedure(p); // catalog.upsert when signed in
  }, []);

  const removeCustomProcedure = useCallback((id: string) => {
    setCustomProcedures((prev) => prev.filter((c) => c.id !== id));
    actionsRef.current.removeRemoteCustomProcedure(id);
  }, []);

  const hydrateFromServer = useCallback(
    async (id: string): Promise<void> => {
      try {
        const row = await utils.surgeries.getById.fetch({ id });
        const payload = row?.payload;
        if (payload && isValidSurgery(payload)) {
          hydrateSurgery(normalizeIngestedSurgery(payload));
        }
      } catch {
        /* offline / not found — keep local summary */
      }
    },
    [utils.surgeries.getById, hydrateSurgery],
  );

  // After listMine, idle-prefetch the newest thin volumes so detail/export feel instant.
  useEffect(() => {
    if (!isAuthenticated || !surgeriesQuery.isSuccess) return;
    const thin = [...surgeriesRef.current]
      .filter(isSurgeryDetailThin)
      .sort(
        (a, b) =>
          b.fecha.localeCompare(a.fecha) || b.actualizadoEn.localeCompare(a.actualizadoEn),
      )
      .slice(0, DETAIL_PREFETCH_N);
    if (thin.length === 0) return;

    let cancelled = false;
    const run = async () => {
      for (const s of thin) {
        if (cancelled) return;
        await hydrateFromServer(s.id);
      }
    };

    const ric = window.requestIdleCallback?.bind(window);
    if (ric) {
      const id = ric(() => {
        void run();
      }, { timeout: 5000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(id);
      };
    }
    const timer = globalThis.setTimeout(() => {
      void run();
    }, 1200);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [isAuthenticated, surgeriesQuery.isSuccess, surgeriesQuery.dataUpdatedAt, hydrateFromServer]);

  const exportFile = useCallback(async (): Promise<string> => {
    if (isAuthenticated) {
      const thin = surgeriesRef.current.filter(isSurgeryDetailThin);
      // Cap concurrent detail fetches so export stays responsive on large archives.
      const queue = thin.slice();
      const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
        while (queue.length > 0) {
          const next = queue.shift();
          if (next) await hydrateFromServer(next.id);
        }
      });
      await Promise.all(workers);
    }
    const year = new Date().getFullYear();
    const filename = `suturia-logbook-${year}.json`;
    const data: ExportFile = {
      app: 'suturia',
      version: 1,
      exportadoEn: new Date().toISOString(),
      perfil: profileRef.current,
      // v1 file-format key, kept so older builds can read new exports
      cirugias: [...surgeriesRef.current].sort((a, b) => a.fecha.localeCompare(b.fecha)),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return filename;
  }, [isAuthenticated, hydrateFromServer]);

  const mergeSurgeries = useCallback((list: Surgery[]): ImportResult => {
    const valid = list
      .filter(isValidSurgery)
      .filter((c) => !isDemoSurgery(c))
      .map(normalizeIngestedSurgery);
    const discarded = list.length - valid.length;
    let added = 0;
    let updated = 0;

    const byId = new Map(surgeriesRef.current.map((c) => [c.id, c]));
    const usedFolios = new Set(surgeriesRef.current.map((c) => c.folio));
    const result = [...surgeriesRef.current];
    const synced: Surgery[] = [];
    for (const incoming of valid) {
      const existing = byId.get(incoming.id);
      if (existing) {
        // Preserve richer local technique when re-importing a skeleton.
        const merged: Surgery = {
          ...incoming,
          pasos:
            (incoming.pasos?.some((p) => p.trim()) ? incoming.pasos : existing.pasos) ?? [],
          materiales:
            incoming.materiales?.length > 0 ? incoming.materiales : existing.materiales,
          descripcion: incoming.descripcion.trim()
            ? incoming.descripcion
            : existing.descripcion,
          ficha: incoming.ficha ?? existing.ficha,
          seguimiento: incoming.seguimiento ?? existing.seguimiento,
          snomedCt: incoming.snomedCt ?? existing.snomedCt,
          creadoEn: existing.creadoEn,
        };
        const idx = result.findIndex((c) => c.id === incoming.id);
        result[idx] = merged;
        updated += 1;
        synced.push(merged);
      } else {
        let copy = incoming;
        if (usedFolios.has(copy.folio)) {
          const year = new Date(copy.fecha).getFullYear();
          copy = {
            ...copy,
            folio: nextFolio(result, Number.isNaN(year) ? new Date().getFullYear() : year),
          };
        }
        usedFolios.add(copy.folio);
        result.push(copy);
        added += 1;
        synced.push(copy);
      }
    }
    setSurgeries(result);
    actionsRef.current.bulkSurgeries(synced); // bulkUpsert when signed in
    return { added, updated, discarded };
  }, []);

  const importFile = useCallback((json: string): ImportResult => {
    let data: Partial<ExportFile> & { surgeries?: Surgery[] };
    try {
      data = JSON.parse(json);
    } catch {
      throw new Error('The file is not valid JSON.');
    }
    // v1 files use the "cirugias" key; tolerate it plus a bare surgery array
    const list = Array.isArray(data.cirugias)
      ? data.cirugias
      : Array.isArray(data.surgeries)
        ? data.surgeries
        : Array.isArray(data)
          ? (data as unknown as Surgery[])
          : null;
    if (!list) throw new Error('The file does not contain any procedures.');

    const result = mergeSurgeries(list);

    if (data.perfil && typeof data.perfil === 'object') {
      const mergedProfile = { ...profileRef.current, ...data.perfil };
      setProfile(mergedProfile);
      actionsRef.current.syncProfile(mergedProfile);
    }
    return result;
  }, [mergeSurgeries]);

  const archiveSummary = useMemo(() => {
    const years = new Set(surgeries.map((c) => new Date(c.fecha).getFullYear()));
    return { procedures: surgeries.length, years: years.size };
  }, [surgeries]);

  // Stable when folio/title fields don't change — chrome can subscribe without
  // re-rendering on pasos/ficha hydration.
  const surgeryIndexKey = surgeries
    .map(
      (s) =>
        `${s.id}\0${s.folio}\0${s.fecha}\0${s.categoria}\0${s.diagnostico}\0${s.tecnica}\0${s.paciente.iniciales}\0${s.paciente.historiaClinicaHint ?? ''}`,
    )
    .join('\n');
  const surgeryIndex = useMemo<SurgeryIndexItem[]>(
    () =>
      surgeries.map((s) => ({
        id: s.id,
        folio: s.folio,
        fecha: s.fecha,
        categoria: s.categoria,
        diagnostico: s.diagnostico,
        tecnica: s.tecnica,
        pacienteCodigo: s.paciente.iniciales,
        ...(s.paciente.historiaClinicaHint
          ? { pacienteIdHint: s.paciente.historiaClinicaHint }
          : {}),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by surgeryIndexKey
    [surgeryIndexKey],
  );

  const surgeriesValue = useMemo<SurgeriesContextValue>(
    () => ({
      surgeries,
      createSurgery,
      updateSurgery,
      deleteSurgery,
      getSurgery,
      hydrateSurgery,
      saveFicha,
      loadSeed,
      exportFile,
      importFile,
      mergeSurgeries,
      archiveSummary,
    }),
    [
      surgeries,
      createSurgery,
      updateSurgery,
      deleteSurgery,
      getSurgery,
      hydrateSurgery,
      saveFicha,
      loadSeed,
      exportFile,
      importFile,
      mergeSurgeries,
      archiveSummary,
    ],
  );

  const profilePrefsValue = useMemo<ProfilePrefsContextValue>(
    () => ({ profile, prefs, saveProfile, updatePrefs }),
    [profile, prefs, saveProfile, updatePrefs],
  );

  const catalogValue = useMemo<CatalogContextValue>(
    () => ({ customProcedures, upsertCustomProcedure, removeCustomProcedure }),
    [customProcedures, upsertCustomProcedure, removeCustomProcedure],
  );

  // Nested providers: prefs/profile updates do not re-render surgery consumers;
  // surgery index is separate so chrome ignores detail hydration.
  // createElement instead of JSX: this module is .ts (no JSX transform)
  return createElement(
    ProfilePrefsContext.Provider,
    { value: profilePrefsValue },
    createElement(
      CatalogContext.Provider,
      { value: catalogValue },
      createElement(
        SurgeryIndexContext.Provider,
        { value: surgeryIndex },
        createElement(SurgeriesContext.Provider, { value: surgeriesValue }, children),
      ),
    ),
  );
}

/* ────────────────────────── hooks ────────────────────────── */

export function useSuturia(): SuturiaContextValue {
  const surgeries = useContext(SurgeriesContext);
  const profilePrefs = useContext(ProfilePrefsContext);
  const catalog = useContext(CatalogContext);
  if (!surgeries || !profilePrefs || !catalog) {
    throw new Error('useSuturia must be used inside <SuturiaProvider>');
  }
  return { ...surgeries, ...profilePrefs, ...catalog };
}

export function useSurgeries() {
  const ctx = useContext(SurgeriesContext);
  if (!ctx) throw new Error('useSurgeries must be used inside <SuturiaProvider>');
  return ctx;
}

export function useProfile() {
  const ctx = useContext(ProfilePrefsContext);
  if (!ctx) throw new Error('useProfile must be used inside <SuturiaProvider>');
  return { profile: ctx.profile, saveProfile: ctx.saveProfile };
}

export function usePrefs() {
  const ctx = useContext(ProfilePrefsContext);
  if (!ctx) throw new Error('usePrefs must be used inside <SuturiaProvider>');
  return { prefs: ctx.prefs, updatePrefs: ctx.updatePrefs };
}

export function useCustomProcedures() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCustomProcedures must be used inside <SuturiaProvider>');
  return {
    customProcedures: ctx.customProcedures,
    upsertCustomProcedure: ctx.upsertCustomProcedure,
    removeCustomProcedure: ctx.removeCustomProcedure,
  };
}

export function useArchive() {
  const { exportFile, importFile, mergeSurgeries, archiveSummary, loadSeed } = useSurgeries();
  return { exportFile, importFile, mergeSurgeries, archiveSummary, loadSeed };
}

/** Lightweight surgery list for Navbar / palette / document title. */
export function useSurgeryIndex(): SurgeryIndexItem[] {
  return useContext(SurgeryIndexContext);
}

/** Test-only exports — keep ingest helpers unit-testable without mounting the provider. */
export const __testables = { withTimestamps, normalizeIngestedSurgery };
